import React from 'react';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const GutterCleaning = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: "Debris Removal",
      description: "Clearing leaves, sticks, and buildup from gutters and downspouts."
    },
    {
      title: "Flush & Flow Check",
      description: "Water testing to confirm proper drainage."
    },
    {
      title: "Downspout Cleaning",
      description: "Clearing clogs that cause backups and overflow."
    },
    {
      title: "Roofline Check",
      description: "Inspecting fascia, soffits, and roof edges for signs of water damage."
    },
    {
      title: "Seasonal Maintenance",
      description: "Spring and fall cleanings to prevent buildup before heavy rains."
    },
    {
      title: "Commercial Gutter Cleaning",
      description: "Reliable service for businesses, restaurants, and commercial properties."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <ServiceHero
        title="Gutter Cleaning"
        headline="Clogged Gutters?"
        description="Protect your home from water damage with professional gutter cleaning that keeps rainwater flowing where it belongs."
        backgroundImage="/images/services/gutter-cleaning.jpg"
        defaultService="Gutter Cleaning"
      />
      
      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6">
                Our Gutter Cleaning Services
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Clogged gutters can cause big problems: water backing up into your roof, fascia 
                damage, foundation issues, even pest infestations. That's why seasonal gutter 
                cleaning is one of the simplest ways to protect your home.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                At <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>, we provide safe, thorough gutter and downspout cleaning for 
                homes and businesses across the East End. Our team makes sure your system is 
                working properly — so the next time it rains, water flows freely away from your 
                home.
              </p>

              <p className="text-gray-700 font-medium mb-4">
                Our gutter cleaning services include:
              </p>

              <ul className="space-y-4 mb-8">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-900 font-semibold">{service.title} – </span>
                      <span className="text-gray-700">{service.description}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mb-8">
                <p className="font-semibold text-gray-900 mb-2">We Also Offer:</p>
                <p className="text-gray-700">
                  Beyond Gutter Cleaning, our team is trusted across Long Island for{' '}
                  <strong>Window Cleaning, House Washing, Roof Washing, and Shrink Wrapping</strong>.{' '}
                  <Link to="/#services" className="text-blue-600 hover:text-blue-700 font-medium">
                    Explore all our services <ArrowRight className="inline w-4 h-4" />
                  </Link>
                </p>
              </div>

              <button
                onClick={scrollToQuote}
                data-testid="gutter-cleaning-cta"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Request a Quote
              </button>
              <p className="mt-3 text-sm text-gray-600">
                Get within 1 business day
              </p>
            </div>

            {/* Right Images */}
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                {/* Main Image - Gutter Before/After */}
                <div className="relative">
                  <img
                    src="/images/before-after/gutter-cleaning.jpg"
                    alt="Gutter cleaning before and after"
                    className="w-full h-[500px] object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Top Right */}
                  <div className="absolute -top-4 -right-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8" />
                      <div>
                        <div className="text-2xl font-bold">15+ Years</div>
                        <div className="text-sm text-blue-100">Of Experience</div>
                      </div>
                    </div>
                  </div>
                  {/* Stats Badge - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-xl">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">1450+ Clients</div>
                        <div className="text-sm text-gray-600">Satisfied By Us</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Protect Your Home?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation quote within 1 business day. 
            Our gutter cleaning experts are ready to keep your home safe from water damage.
          </p>
          <Link
            to="/#quote"
            data-testid="gutter-cleaning-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default GutterCleaning;
